import styled, { keyframes } from "styled-components";
import { theme } from "../../styles/theme";

// Floating top toolbar chrome — aligned to the canvas toolbar mockup
// (pill groups, violet Run CTA, coral Back, amber Issues badge).
const GROUP_BG = theme.surface.groupBg;
const GROUP_BORDER = theme.surface.groupBorder;
const HOVER_FILL = theme.surface.hoverFill;
const MUTED_TEXT = theme.surface.mutedText;
const FLOAT = theme.shadows.float;

const badgePulse = keyframes`
   0%, 100% { box-shadow: 0 3px 8px -2px rgba(232, 150, 60, 0.6); }
   50% { box-shadow: 0 3px 14px -1px rgba(232, 150, 60, 0.9); }
`;

export const Main = styled.div`
   position: absolute;
   top: 22px;
   left: 50%;
   transform: translateX(-50%);
   z-index: 999;
   width: max-content;
   max-width: calc(100% - 48px);
   transition: left 0.32s cubic-bezier(0.2, 0.9, 0.25, 1);

   /* Expanded sidebar: center in the free space between panel and viewport right */
   html[data-wf-sidebar='expanded'] & {
      left: calc(var(--wf-sidebar-end, 428px) + (100% - var(--wf-sidebar-end, 428px)) / 2);
      max-width: calc(100% - var(--wf-sidebar-end, 428px) - 24px);
   }

   i.ti {
      display: inline-flex;
      font-size: 15px;
      line-height: 1;
   }

   > .flex {
      display: flex;
      align-items: center;
      gap: 10px;
      flex-wrap: wrap;
      justify-content: center;

      .back {
         display: flex;
         align-items: center;
         gap: 7px;
         height: auto;
         padding: 10px 6px;
         border-radius: 999px;
         background-color: transparent;
         color: ${theme.colors.coral};
         font-size: 13.5px;
         font-weight: 700;
         cursor: pointer;
         transition: opacity 0.16s ease;

         span {
            margin-left: 0;
         }
         i.ti {
            transition: transform 0.16s ease;
         }
         &:hover {
            background-color: transparent;
            opacity: 0.75;

            i.ti {
               transform: translateX(-2px);
            }
         }
      }

      .group {
         display: flex;
         align-items: center;
         gap: 2px;
         height: auto;
         min-height: 0;
         padding: 6px;
         background-color: ${GROUP_BG};
         border-radius: 999px;
         border: 1px solid ${GROUP_BORDER};
         box-shadow: ${FLOAT};
      }

      .layout-toggle {
         display: flex;
         align-items: center;
         justify-content: center;
         width: 44px;
         height: 44px;
         border-radius: 50%;
         background-color: ${GROUP_BG};
         border: 1px solid ${GROUP_BORDER};
         color: ${theme.colors.textSecondary};
         cursor: pointer;
         box-shadow: ${FLOAT};
         transition: color 0.16s ease, transform 0.16s ease;

         &:hover {
            background-color: ${GROUP_BG};
            color: ${theme.colors.primary};
            transform: translateY(-1px);
         }
      }
   }
   > .flex .group {
      li {
         display: flex;
         align-items: center;
         justify-content: center;
         gap: 7px;
         height: auto;
         min-height: 36px;
         padding: 9px 16px;
         border-radius: 999px;
         font-size: 13.5px;
         font-weight: 600;
         color: ${theme.colors.textSecondary};
         cursor: pointer;
         transition: background-color 0.16s ease, color 0.16s ease, transform 0.16s ease;
         white-space: nowrap;
         position: relative;

         span {
            margin-left: 0;
         }
         &:hover {
            background-color: ${HOVER_FILL};
            color: ${theme.colors.text};
         }

         &.run {
            background: ${theme.gradients.primary};
            color: #fff;
            box-shadow:
               0 1px 0 rgba(255, 255, 255, 0.3) inset,
               0 -3px 6px rgba(0, 0, 0, 0.14) inset,
               0 10px 20px -8px rgba(79, 63, 224, 0.55);

            &:hover {
               background: ${theme.gradients.primary};
               opacity: 1;
               color: #fff;
               transform: translateY(-1px);
            }
         }
         &.running {
            svg {
               animation: run 1.1s infinite linear;
               margin-right: 0;

               @keyframes run  {
                  0% { transform: rotate(0deg); }
                  100% { transform: rotate(360deg); }
               }
            }
         }
         &.loop {
            background-color: ${HOVER_FILL};
            color: ${theme.colors.text};
            font-weight: 700;
         }
         &.issues {
            position: relative;

            .issues-badge {
               display: inline-flex;
               align-items: center;
               justify-content: center;
               min-width: 19px;
               height: 19px;
               margin-left: 0;
               padding: 0 5px;
               border-radius: 999px;
               background: linear-gradient(150deg, #FFA857, ${theme.colors.amber});
               color: #fff;
               font-size: 10.5px;
               font-style: normal;
               font-weight: 700;
               line-height: 1;
               box-shadow: 0 3px 8px -2px rgba(232, 150, 60, 0.6);
               animation: ${badgePulse} 2s ease-in-out infinite;
            }

            &.has-warnings .issues-badge {
               background: linear-gradient(150deg, #FFA857, ${theme.colors.amber});
            }

            &.has-errors .issues-badge {
               background: linear-gradient(150deg, #F87171, ${theme.colors.coral});
               box-shadow: 0 3px 8px -2px rgba(217, 87, 63, 0.55);
               animation: none;
            }

            &.has-errors {
               background-color: rgba(254, 226, 226, 0.55);
               color: #b91c1c;
               font-weight: 700;

               &:hover {
                  background-color: rgba(254, 226, 226, 0.9);
                  color: #991b1b;
               }
            }

            &.has-warnings:not(.has-errors) {
               color: #c2410c;

               &:hover {
                  background-color: rgba(255, 237, 213, 0.7);
                  color: #9a3412;
               }
            }
         }
         &.disabled {
            pointer-events: none;
         }

         &.icon-only {
            padding: 0;
            width: 36px;
            height: 36px;
            min-height: 36px;
         }
      }

      &.icon-only li {
         padding: 0;
         width: 36px;
         height: 36px;
         min-height: 36px;
      }
      &.icon-only li.muted {
         color: ${MUTED_TEXT};
         opacity: 0.5;
         cursor: not-allowed;

         &:hover {
            background-color: transparent;
            color: ${MUTED_TEXT};
         }
      }
   }

   @media (max-width: 900px) {
      > .flex .group li span,
      > .flex .back span {
         display: none;
      }
      > .flex .group li {
         padding: 0;
         width: 36px;
         height: 36px;
         min-height: 36px;
      }
      > .flex .back {
         width: auto;
         padding: 10px 8px;
      }
   }
   .popup {
      display: flex;
      justify-content: center;
      align-items: center;
      position: fixed;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      background-color: rgba(30, 39, 46, 0.65);
      z-index: 99;

      > div {
         position: relative;
         background-color: #fff;
         padding: 30px;
         border-radius: 5px;
         box-shadow: 0 1px 3px rgba(0,0,0,0.12), 0 1px 2px rgba(0,0,0,0.24);

         .close {
            display: flex;
            align-items: center;
            justify-content: center;
            position: absolute;
            top: 25px;
            right: 30px;
            transition: all .2s ease-in-out;
            cursor: pointer;
            width: 32px;
            height: 32px;
            border-radius: 7.5px;
            opacity: 0.8;

            &:hover {
               background-color: rgba(241, 245, 249, 1);
               opacity: 1;
            }
         }
         .content {
            .title {
               display: flex;
               align-items: center;
               margin-bottom: 20px;

               .icon {
                  display: flex;
                  align-items: center;
                  justify-content: center;
                  width: 36px;
                  height: 36px;
                  background-color: rgba(219, 234, 254, 1);
                  color: rgba(37, 99, 235, 1);
                  border-radius: 5px;
                  margin-right: 10px;
               }
               h3 {
                  font-weight: 400;
                  font-size: 19px;
               }
            }
         }
      }
   }
   .reset {
      > div {
         width: 455px;

         .content {
            .title .icon {
               background-color: rgba(254, 226, 226, 1);
               color: rgba(220, 38, 38, 1);
            }
            .info {
               > div:first-of-type {
                  box-shadow: 0 1px 2px 0 rgba(0, 0, 0, 0.05);
                  background-color: rgba(254, 242, 242, 1);
                  border: 1px solid rgba(254, 202, 202, 1);
                  border-radius: 7.5px;
                  padding: 15px;
                  margin-bottom: 25px;

                  span:first-of-type {
                     display: block;
                     font-size: 15px;
                     font-weight: 500;
                     margin-bottom: 10px;
                     color: rgba(153, 27, 27, 1);
                  }
                  span:last-of-type {
                     font-size: 15px;
                     color: rgba(185, 28, 28, 1);
                  }
               }
               label {
                  display: block;
                  margin-bottom: 7.5px;
                  font-size: 16px;
               }
               .textInput {
                  margin-bottom: 25px;
                  input {
                     background-color: transparent;
                     height: 40px;
                     font-size: 15px !important;
                     box-shadow: 0 1px 2px 0 rgba(0, 0, 0, 0.05);
                     border: 1px solid rgba(226, 232, 240, 1);
                  }
               }
            }
            .tip {
               display: flex;
               font-size: 17px;
               margin-bottom: 25px;
               box-shadow: 0 1px 2px 0 rgba(0, 0, 0, 0.05);
               border-radius: 7.5px;
               padding: 15px;
               font-size: 15px;
               margin-bottom: 25px;
               line-height: 1.4;
               background-color: rgba(255, 251, 235, 1);
               border: 1px solid rgba(253, 230, 138, 1);
               color: rgba(146, 64, 14, 1);

               > span:first-of-type {
                  min-width: 28px;

                  svg {
                     color: rgba(217, 119, 6, 1);
                  }
               }
               > span:last-of-type span {
                  font-weight: 500;
               }
            }
            .action {
               display: flex;
               justify-content: space-between;

               button {
                  display: flex;
                  align-items: center;
                  justify-content: center;
                  width: calc((100% / 2) - 7.5px);
                  height: 40px;
                  border: 1px solid transparent;
                  border-radius: 5px;
                  background-color: rgba(220, 38, 38, 1);
                  color: #fff;
                  transition: all .2s ease-in-out;
                  cursor: pointer;
                  font-size: 14px;

                  &.cancel {
                     background-color: transparent;
                     color: initial;
                     border-color: rgba(226, 232, 240, 1);
                     box-shadow: 0 1px 2px 0 rgba(0, 0, 0, 0.05);

                     &:hover {
                        background-color: rgba(248, 250, 252, 1);
                        opacity: 1;
                     }
                  }
                  &:disabled {
                     color: rgba(100, 116, 139, 1);
                     background-color: rgba(203, 213, 225, 1);
                     opacity: 0.65;
                     cursor: default;
                     &:hover { opacity: 0.65; }
                  }
                  &:hover {
                     opacity: 0.9;
                  }
               }
            }
         }
      }
   }
`;
